<template>
  <q-card flat bordered>
    <div id="intercom-header" class="q-pa-md flex justify-between q-gutter-y-sm">
      <div>
        <div class="text-h6">Fees</div>
        <div style="font-size: 12px">
          Compare fees charged by your subject property to what the market is charging.
        </div>
      </div>
      <div id="intercom-fees-actions" data-html2canvas-ignore>
        <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
          <q-icon name="download" class="q-mr-sm" size="medium" />Download
        </q-btn>
      </div>
    </div>
    <q-table
      id="intercom-fees-content"
      :rows="rows"
      :columns="columns"
      :rows-per-page-options="[0]"
      hide-bottom
      bordered
      dense
      flat
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #body="props">
        <q-tr
          :props="props"
          :class="{
            'text-bold bg-deep-purple-light text-primary':
              subjectDetail && props.row.id === subjectDetail.id
          }"
        >
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <div>{{ col.value }}</div>
          </q-td>
        </q-tr>
        <q-tr
          v-if="props.rowIndex === rows.length - 1"
          class="text-bold text-center title-row"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <q-td class="text-left">
            <div>
              Comp Average
              <q-icon v-if="subjectDetail" name="info_outline">
                <q-tooltip>
                  Does not include {{ subjectDetail.building_name || subjectDetail.street_address }}
                </q-tooltip>
              </q-icon>
            </div>
          </q-td>
          <q-td v-for="(val, i) in footerRow" :key="i">
            {{ val }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { QTableColumn } from 'quasar'
import type { PropertyDetailsResponse, Availability, AvailabilityPeriod, Price } from 'common'
import { formatMoney } from '@/utils'

export default {
  name: 'PropertyCompareFees',
  components: {},
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    }
  },
  computed: {
    footerRow(): (string | null)[] {
      if (this.columns) {
        return this.columns.slice(1).map((col) => {
          const strVals = this.rows
            .filter((_) => _.id !== this.subjectDetail?.id)
            .map((row) => (col.format ? col.format(row[col.field], row) : row[col.field]))
            .filter((_) => _)
            .map(String)
          const vals = strVals
            .map((_) =>
              _.split('$').join('').replace('Cat', '').replace('Dog', '').split(',').join('')
            )
            .flatMap((_) => _.split('/'))
            .map(parseFloat)
            .filter((_) => !isNaN(_))
          if (!vals.length) {
            return null
          }
          return formatMoney(Math.round(vals.reduce((a, b) => a + b) / vals.length))
        })
      }
      return []
    },
    rows() {
      return this.filteredProperties.flatMap(({ prop }) => ({
        id: prop.id,
        label: prop.building_name || prop.street_address,
        application_fee: prop.application_fee || null,
        admin_fee: prop.admin_fee || null,
        amenity_fee: prop.amenity_fee || null,
        storage_fee: prop.storage_fee || null,
        pet_rent: [prop.cats_monthly_rent || null, prop.dogs_monthly_rent || null],
        pet_one_time_fee: [prop.cats_one_time_fee || null, prop.dogs_one_time_fee || null],
        pet_deposit: [prop.cats_deposit || null, prop.dogs_deposit || null],
        parking_covered: prop.parking_covered || null,
        parking_garage: prop.parking_garage || null,
        parking_surface_lot: prop.parking_surface_lot || null
      }))
    },
    columns() {
      return [
        { name: '', label: '', field: 'label', align: 'left', classes: 'constrained-cell' },
        {
          name: 'Application Fee',
          label: 'Application Fee',
          field: 'application_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Admin Fee',
          label: 'Admin Fee',
          field: 'admin_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Amenity Fee',
          label: 'Amenity Fee',
          field: 'amenity_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Storage Fee',
          label: 'Storage Fee',
          field: 'storage_fee',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Pet Rent',
          label: 'Pet Rent',
          field: (row) => row.pet_rent[0],
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (_, row) => this.formatPets(row.pet_rent)
        },
        {
          name: 'Pet Fee',
          label: 'Pet Fee',
          field: (row) => row.pet_one_time_fee[0],
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (_, row) => this.formatPets(row.pet_one_time_fee)
        },
        {
          name: 'Pet Deposit',
          label: 'Pet Deposit',
          field: (row) => row.pet_deposit[0],
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (_, row) => this.formatPets(row.pet_deposit)
        },
        {
          name: 'Covered Parking',
          label: 'Covered Parking',
          field: 'parking_covered',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Garage Parking',
          label: 'Garage Parking',
          field: 'parking_garage',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        },
        {
          name: 'Surface Lot Parking',
          label: 'Surface Lot Parking',
          field: 'parking_surface_lot',
          align: 'center',
          sortable: true,
          sortOrder: 'da',
          format: (x: number | null) => formatMoney(x)
        }
      ] as QTableColumn[]
    }
  },
  methods: {
    formatPets(range: [number | null, number | null]): string {
      if (range[0] === range[1]) {
        return formatMoney(range[0])
      }
      return `${formatMoney(range[0])} Cat / ${formatMoney(range[1])} Dog`
    },
    exportCSV() {
      const headers = this.columns.map((col) => col.label)
      const csv = this.rows
        .map((row) =>
          this.columns
            .map((col) => (col.format ? col.format(row[col.field], row) : row[col.field]))
            .join(',')
        )
        .join('\n')
      const blob = new Blob([headers.join(',') + '\n' + csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `hello-data-fees-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
    }
  }
}
</script>
<style scoped>
.title-row td {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
